import { AGENT_API_BASE_URL, AGENT_API_KEY, AGENT_REFRESH_PATH } from './agentConfig';
import { getApiErrorMessage } from './apiMessages';
import { clearAgentToken, getAgentToken, setAgentToken } from './tokenStore';

function buildUrl(path) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }

  const normalizedBase = AGENT_API_BASE_URL.endsWith('/')
    ? AGENT_API_BASE_URL
    : `${AGENT_API_BASE_URL}/`;
  const normalizedPath = path.startsWith('/') ? path.slice(1) : path;

  return `${normalizedBase}${normalizedPath}`;
}

function appendParams(path, params = {}) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== '');
  if (entries.length === 0) {
    return path;
  }

  const query = new URLSearchParams();
  entries.forEach(([key, value]) => {
    query.set(key, String(value));
  });

  return `${path}${path.includes('?') ? '&' : '?'}${query.toString()}`;
}

function isApiKeyFailure(payload) {
  const errorCode = payload?.error || '';
  const message = String(payload?.message || '');
  return (
    errorCode === 'MissingAPIKey' ||
    errorCode === 'InvalidAPIKey' ||
    /api key/i.test(message)
  );
}

function requestHeaders({ body, headers = {}, token }) {
  return {
    ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(AGENT_API_KEY ? { 'x-api-key': AGENT_API_KEY } : {}),
    ...headers,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function payloadErrorMessage(payload, fallback = 'Request failed') {
  if (typeof payload === 'object' && payload) {
    return payload.message || payload.error || payload.errors?.[0]?.message || fallback;
  }
  return fallback;
}

function createApiError({ status, payload, fallback, sessionExpired, isNetworkError }) {
  const error = new Error(payloadErrorMessage(payload, fallback));
  error.status = status;
  error.payload = payload;
  error.rawMessage = error.message;
  error.sessionExpired = sessionExpired;
  error.isNetworkError = Boolean(isNetworkError);
  error.message = getApiErrorMessage(error, fallback);
  return error;
}

function networkError() {
  return createApiError({
    isNetworkError: true,
    fallback: 'NETWORK',
  });
}

async function parseResponse(response, { fallback, sessionExpired } = {}) {
  const contentType = response.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw createApiError({
      status: response.status,
      payload,
      fallback,
      sessionExpired,
    });
  }

  return payload;
}

function parseXhrPayload(xhr) {
  const contentType = xhr.getResponseHeader('content-type') || '';
  if (contentType.includes('application/json') && xhr.responseText) {
    try {
      return JSON.parse(xhr.responseText);
    } catch (_error) {
      return xhr.responseText;
    }
  }
  return xhr.responseText;
}

function requestWithProgress(path, options = {}) {
  const {
    body,
    headers = {},
    authToken,
    params,
    method = 'POST',
    onUploadProgress,
    retryOnUnauthorized = true,
  } = options;

  const token = authToken === undefined ? getAgentToken() : authToken;
  const url = buildUrl(appendParams(path, params));

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    xhr.withCredentials = true;

    Object.entries(requestHeaders({ body, headers, token })).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });

    xhr.upload.onprogress = (event) => {
      if (typeof onUploadProgress !== 'function') return;
      if (event.lengthComputable && event.total > 0) {
        onUploadProgress(Math.min(99, Math.round((event.loaded / event.total) * 100)));
        return;
      }
      onUploadProgress(50);
    };

    xhr.upload.onload = () => {
      if (typeof onUploadProgress === 'function') onUploadProgress(100);
    };

    xhr.onload = async () => {
      const payload = parseXhrPayload(xhr);

      if (xhr.status === 401 && token) {
        if (!isApiKeyFailure(payload) && retryOnUnauthorized && !isRefreshPath(path)) {
          try {
            const nextToken = await refreshAccessToken();
            resolve(requestWithProgress(path, {
              ...options,
              authToken: nextToken,
              retryOnUnauthorized: false,
            }));
            return;
          } catch (_refreshError) {
            clearAgentToken();
          }
        } else if (!isApiKeyFailure(payload)) {
          clearAgentToken();
        }
      }

      if (xhr.status < 200 || xhr.status >= 300) {
        reject(createApiError({
          status: xhr.status,
          payload,
          fallback: 'UPLOAD_DOCUMENT',
          sessionExpired: xhr.status === 401 ? Boolean(token) : undefined,
        }));
        return;
      }

      resolve(payload);
    };

    xhr.onerror = () => reject(networkError());
    xhr.onabort = () => reject(createApiError({ fallback: 'CANCEL_UPLOAD' }));
    xhr.send(body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : null);
  });
}

function isRefreshPath(path) {
  return String(path || '').includes('/travel-partner/refresh-token');
}

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    let response;
    try {
      response = await fetch(buildUrl(AGENT_REFRESH_PATH), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(AGENT_API_KEY ? { 'x-api-key': AGENT_API_KEY } : {}),
        },
        body: JSON.stringify({}),
      });
    } catch (_networkError) {
      throw networkError();
    }

    const payload = await parseResponse(response, {
      fallback: 'SESSION_EXPIRED',
      sessionExpired: true,
    });
    const token = payload?.data?.token || payload?.token;
    if (!token) {
      throw createApiError({
        status: 401,
        fallback: 'SESSION_EXPIRED',
        sessionExpired: true,
      });
    }

    setAgentToken(token);
    return token;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function request(path, options = {}) {
  const {
    body,
    headers = {},
    authToken,
    params,
    responseType,
    retryOnUnauthorized = true,
    onUploadProgress,
    ...rest
  } = options;

  if (typeof onUploadProgress === 'function') {
    return requestWithProgress(path, options);
  }

  const token = authToken === undefined ? getAgentToken() : authToken;

  let response;
  try {
    response = await fetch(buildUrl(appendParams(path, params)), {
      credentials: 'include',
      ...rest,
      headers: requestHeaders({ body, headers, token }),
      ...(body !== undefined
        ? { body: body instanceof FormData ? body : JSON.stringify(body) }
        : {}),
    });
  } catch (_networkError) {
    throw networkError();
  }

  if (response.status === 401 && token) {
    let payload = null;
    try {
      payload = await response.clone().json();
    } catch (error) {
      payload = null;
    }

    if (!isApiKeyFailure(payload) && retryOnUnauthorized && !isRefreshPath(path)) {
      try {
        const nextToken = await refreshAccessToken();
        return request(path, {
          ...options,
          authToken: nextToken,
          retryOnUnauthorized: false,
        });
      } catch (refreshError) {
        clearAgentToken();
      }
    } else if (!isApiKeyFailure(payload)) {
      clearAgentToken();
    }
  }

  const sessionExpired = response.status === 401 ? Boolean(token) : undefined;

  if (responseType === 'blob') {
    if (!response.ok) {
      await parseResponse(response, { sessionExpired });
    }
    return response.blob();
  }

  return parseResponse(response, { sessionExpired });
}

export const agentApi = {
  get(path, options = {}) {
    return request(path, {
      method: 'GET',
      ...options,
    });
  },
  post(path, options = {}) {
    return request(path, {
      method: 'POST',
      ...options,
    });
  },
  put(path, options = {}) {
    return request(path, {
      method: 'PUT',
      ...options,
    });
  },
  delete(path, options = {}) {
    return request(path, {
      method: 'DELETE',
      ...options,
    });
  },
};
